import { action } from '@ember/object';
import { debounce, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, DateRangeInterface, DSSetup, RecordData, Service } from 'webapp-types';
import { ModelName, TableColumnType } from 'webapp/enums/enums';
import { copyObject, equal } from 'webapp/utils/common';

import type Block from 'webapp/models-custom/block';
import type { Changes as BlockChanges } from 'webapp/models-custom/block-state';
import BlockState from 'webapp/models-custom/block-state';
import type Board from 'webapp/models-custom/board';
import DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';
import Serviced from 'webapp/models-custom/serviced';
import type { ColorSetting } from 'webapp/models/block';
import type BoardRecord from 'webapp/models/board';
import type SampleRecord from 'webapp/models/sample';

// Used with preview boards / blocks to record changes (some temporary) in the preview itself
// e.g. master date range on board, or selected "tab" index in main block
// these changes then are either used to create board preview or directly update metricsettings (in designer)

// Board, or faked for single blocks
export interface MinimumViableBoard {
	allBlockObjs: Block[];
	directPropUpdate: () => void;
	id?: string;
	isFake?: boolean;
	isPreview?: boolean;
	isMyPreview?: boolean;
	boardId?: number;
	previewHash?: string;
	allowAnonymousDateRangeEditOnDatawall?: boolean;
	allowAnonymousFilterEditOnDatawall?: boolean;
	commonBlockDateRange?: DateRange;
	changes?: Changes;
	boardIdForPublicUrl?: string;
}

export interface MasterMetricSetup {
	dimensions?: string[]; // undefined means "keep current"
	filters: DSSetup.MetricSetupFiltersData | null;
	// unused for now. [{color: "#asd", dimensionValue: "aa"}] <-- migration "dimension" -> "dimensionValue"?
	colors?: Omit<ColorSetting, 'dataSelectId'>[];
}

export interface Changes {
	masterDateRange?: {
		dateRange: DateRangeInterface.FormulaInterface;
		compares?: DSSetup.Compare['dateRange'][];
	};
	masterMetricSetup?: MasterMetricSetup;
}

export interface BlockChangesPayload {
	id?: number; // either id or originateFrom is mandatory
	originateFrom?: number | string;
	changes: BlockChanges;
}

interface RequestPayload {
	boardPreview?: {
		originateFrom?: number;
		changes?: Changes;
	};
	blockPreviews?: BlockChangesPayload[];
}

export default class BoardState extends Serviced {
	@service('store') declare storeService: Service.Store;
	@service('socket') declare socketService: Service.Socket;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/block-previews')
	declare mmBlockPreviewsService: Service.ModelManager.BlockPreviews;

	@tracked board: Board | MinimumViableBoard;

	@tracked private blockStates: BlockState[] = [];

	@tracked selectedBlockState: BlockState | null = null;

	@tracked _selectedMasterDateRange: DateRange | null = null;

	@tracked _selectedMasterCompare: DsCompare | null = null;

	@tracked _selectedMasterDimensions: string[] | undefined = undefined; // undefined means no changes, empty array means dimension "None“ selected

	@tracked _selectedMasterFilters: DSSetup.MetricSetupFiltersData | null | undefined = undefined; // undefined means no changes, bcos null is valid "no filters" state (force no filters on whole board)

	@tracked forceLoader = false; // used with temporary board-states, forcing loader

	// if true, board title cannot be edited, blocks cannot be selected, sorted etc.
	// if false, everything is editable directly (designer edit board), previews are not generated (allow<x>Previews is not even checked) and we can/must provide `onStateChange` method that handles the changes manually
	@tracked isViewOnly = true;

	isReportEditor = false;

	// set to true as soon as user starts creating a preview
	// we use this temporary flag as fallback in case `previewCreatedBy` is not returned yet or preview was set by anonymous
	isMyPreview = false;

	// the last update request so we can chain them
	_updatePreviewPromise: Promise<unknown> | null = null;

	constructor({ board }: { board: Board | MinimumViableBoard }) {
		super();

		this.board = board;

		if (this.board.isPreview && !this.navigationService.isLoadedFromSnapshooter) {
			this._updatePreviewPromise = this._subscribeToPreviewChannel(this.board.previewHash!);
		}
	}

	get boardCM() {
		// for those cases when we know it's not MinimumViableBoard
		return this.board as Board;
	}

	get blockStatesForThisHash() {
		// for unknown reasons sometimes resetting board state doesn't clean up blockStates
		return this.blockStates.filter((bs) => bs.block.previewHash === this.board.previewHash);
	}

	@cached
	get allowAdvancedSetup() {
		return this.blockStatesForThisHash.some((blockState) => blockState.allowAdvancedSetup);
	}

	@cached
	get allowAdvancedFilters() {
		return this.blockStatesForThisHash.some((blockState) => blockState.allowAdvancedFilters);
	}

	@cached
	get metricData() {
		const metricData: DSSetup.MetricData[] = [];
		this.blockStatesForThisHash.forEach((blockState) => {
			blockState.metricData.forEach((md) => {
				if (md && !metricData.find((md2) => equal(md, md2))) {
					metricData.push(md);
				}
			});
		});
		return metricData;
	}

	@cached
	get metricSetupDataArray() {
		// with duplicates
		const metricSetupData: DSSetup.MetricSetupData[] = [];
		this.blockStatesForThisHash.forEach((blockState) => {
			blockState.metricSetupDataArray.forEach((msd) => {
				if (msd) {
					metricSetupData.push(msd);
				}
			});
		});
		return metricSetupData;
	}

	// ------ preview permissions ------
	// is checked for !isViewOnly board states

	@tracked explicitlyDisableAllPreviews = false;

	get allowDateRangePreviews() {
		// when true, shows MDR and allows dateRange changes on blocks
		return this.checkAllowPreviews('allowAnonymousDateRangeEditOnDatawall');
	}

	get allowFilterPreviews() {
		// when true, allows master filter and block filters changes
		return this.checkAllowPreviews('allowAnonymousFilterEditOnDatawall');
	}

	get allowAnyPreviews() {
		return this.allowDateRangePreviews || this.allowFilterPreviews;
	}

	private checkAllowPreviews(
		propName?: 'allowAnonymousDateRangeEditOnDatawall' | 'allowAnonymousFilterEditOnDatawall',
	): boolean {
		let allow = true;

		// previewing will be disabled when explicitly set
		// pr when user is not logged and allowAnonymousEdit is not on
		if (this.explicitlyDisableAllPreviews) {
			allow = false;
		} else if (propName && this.board[propName]) {
			allow = true;
		} else if (this.navigationService.isDatawallApp) {
			allow = this.navigationService.isDatawallAndHasAccessToBoard;
		} else {
			allow = this.currentUserService.isLoggedIn;
		}

		return allow;
	}

	// common for the blocks on the board
	get commonMasterDateRange() {
		return this.board.commonBlockDateRange;
	}

	// selected from the changes
	get selectedMasterDateRange() {
		return this._selectedMasterDateRange || null;
	}

	set selectedMasterDateRange(value) {
		this._selectedMasterDateRange = value;
		this.stateChanged();
	}

	get selectedMasterCompare() {
		return this._selectedMasterCompare || null;
	}

	get selectedMasterDimensions() {
		return this._selectedMasterDimensions;
	}

	get selectedMasterFilters() {
		return this._selectedMasterFilters;
	}

	get selectedBlock() {
		return this.selectedBlockState ? this.selectedBlockState.block : null;
	}

	get isLoadingData() {
		let isLoading = this.forceLoader;

		if (!isLoading) {
			const blockStates = this.blockStates;

			if (blockStates.length) {
				// no blocks have data loaded
				isLoading = !blockStates.some((blockState) => {
					return !blockState.isLoadingData;
				});
			}
		}

		return isLoading;
	}

	get isBlocksLoadingData() {
		// any block is still loading
		return this.blockStates.some((blockState) => {
			return blockState.isLoadingData;
		});
	}

	updateMasterDateRangeSettings(dateRange: DateRange | null, compare: DsCompare | null) {
		this._selectedMasterDateRange = dateRange;
		this._selectedMasterCompare = compare;
		this.stateChanged();
	}

	// when resetting, the second parameter will be null
	updateMasterMetricSetupSettings(dimensions: null, filters: null): void;

	// when setting, the filters parameter can be anything
	updateMasterMetricSetupSettings(
		dimensions: string[] | undefined,
		filters: DSSetup.MetricSetupFiltersData | null,
	): void;

	updateMasterMetricSetupSettings(
		dimensions: string[] | null | undefined,
		filters: DSSetup.MetricSetupFiltersData | null,
	) {
		// dimensions null means reset
		const isReset = dimensions === null;

		if (isReset && !this.board.changes?.masterDateRange) {
			// used "Reset" on master filter -- if no masterDateRange set, do the total reset
			this.resetToOriginal();
		} else {
			this._selectedMasterDimensions = dimensions || undefined;
			this._selectedMasterFilters = filters;

			// extra change for table blocks when switching master dimensions (tableColumnType)
			// for more info, see mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange
			const blocksToSwitchToAuto =
				this.mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange(
					this.blockStates.map((bs) => bs.block) || [],
					dimensions || null,
				);
			blocksToSwitchToAuto.forEach((block) => {
				const blockState = this.getBlockState(block);
				if (blockState) {
					blockState.updateSettings({
						tableColumnType: TableColumnType.AUTO,
					});
				}
			});

			this.stateChanged();
		}
	}

	createNewBlockState(blockCM: Block) {
		const blockState = new BlockState(blockCM);
		blockState.triggerBoardStateChange = this.stateChanged;
		blockState.boardState = this;
		return blockState;
	}

	@action
	stateChanged() {
		// not checking the preview settings bcos we always allow changing visualization

		if (this.revertingToOriginal) {
			return;
		}

		if (this.isViewOnly) {
			debounce(this.updatePreview, 100);
		} else {
			const changes = this._getChangesPayload();
			this.onStateChange(changes);
		}
	}

	@action
	updatePreview() {
		if (this.explicitlyDisableAllPreviews) {
			return;
		}

		let promise = this._updatePreviewPromise;

		if (this.board.isPreview) {
			if (!promise) {
				promise = Promise.resolve();
			}

			// check if we can edit this preview, or must create a copy
			if (this.isMyPreview || (this.board.isMyPreview && this.isReportEditor)) {
				promise = promise.then(() => {
					void this._updateExistingPreview();
				});
			} else {
				// make a copy of the preview when the user is not the owner of the preview
				promise = this._createNewPreview(true);
			}
		} else {
			if (promise) {
				promise = promise.then(() => {
					void this._updateExistingPreview();
				});
			} else {
				// creating new
				promise = this._createNewPreview();
			}
		}

		this._updatePreviewPromise = promise;
	}

	selectBlock(id: string | null) {
		let blockState = null;

		if (id) {
			const block = this.board.allBlockObjs.find((item) => item.id === id);
			if (block) {
				blockState = this.getBlockState(block);
			}
		}

		this.selectedBlockState = blockState || null;
	}

	getBlockState(blockCM: Block) {
		let blockState;

		if (blockCM?.id) {
			blockState = this.blockStates.find((bs) => bs.id === blockCM.numericId);
			if (!blockState) {
				blockState = this.createNewBlockState(blockCM);

				// so upon loading board data they're all added in the same runloop
				scheduleOnce('actions', this, this.addBlockState, blockState);
			}
		}

		return blockState;
	}

	@action
	private addBlockState(blockState: BlockState) {
		if (!this.blockStates.includes(blockState)) {
			this.blockStates = [...this.blockStates, blockState];
		}
	}

	onPreviewHashChange(_hash: string | null) {
		// overwritten from outside
	}

	onStateChange(_payload: RequestPayload) {
		// overwritten from outside
	}

	private _savedPreviewDateRangeFormula: DateRangeInterface.FormulaInterface | null = null;

	_checkBoardPreviewChanges() {
		let hasChanges = false;
		const changes: Changes = {};

		// master date range switch
		let savedPreviewDateRangeFormula;
		if (this.board.isPreview && this.board.changes?.masterDateRange) {
			savedPreviewDateRangeFormula = this.board.changes.masterDateRange.dateRange;
		} else if (!this.isViewOnly) {
			// in designer - master date range is not saved on any board previews, so we cache it here
			savedPreviewDateRangeFormula = this._savedPreviewDateRangeFormula;
			this._savedPreviewDateRangeFormula = this.selectedMasterDateRange
				? this.selectedMasterDateRange.formula
				: null;
		}

		if (this.selectedMasterDateRange) {
			if (
				!savedPreviewDateRangeFormula ||
				!this.selectedMasterDateRange.isEqual(savedPreviewDateRangeFormula) ||
				this.selectedMasterCompare?.formula !== null
			) {
				hasChanges = true;
			}

			const compareFormula = this.selectedMasterCompare?.getFormula();
			changes.masterDateRange = {
				dateRange: this.selectedMasterDateRange.getFormula(),
				compares: compareFormula?.dateRange ? [compareFormula.dateRange] : undefined, // none is purposefully not supported
			};
		} else if (savedPreviewDateRangeFormula) {
			hasChanges = true; // resetting to default
		}

		if (
			!equal(
				this.selectedMasterDimensions || null,
				this.board.changes?.masterMetricSetup?.dimensions || null,
			)
		) {
			hasChanges = true;
		}

		// undefined board.changes filters and selected filters null (means explicitly empty) must be recongized as having changed
		// null board.changes filters and selected filters undefined (means no change) must NOT be recongized as having changed
		if (
			this.selectedMasterFilters === null &&
			this.board.changes?.masterMetricSetup?.filters === undefined
		) {
			hasChanges = true;
		} else {
			if (
				!equal(
					this.selectedMasterFilters || null,
					this.board.changes?.masterMetricSetup?.filters || null,
				)
			) {
				hasChanges = true;
			}
		}

		const masterMetricSetup: Partial<MasterMetricSetup> = {};

		if (this.selectedMasterDimensions) {
			masterMetricSetup.dimensions = this.selectedMasterDimensions;
		}
		if (this.selectedMasterFilters !== undefined) {
			masterMetricSetup.filters = this.selectedMasterFilters;
		}

		if (Object.keys(masterMetricSetup).length) {
			changes.masterMetricSetup = masterMetricSetup as MasterMetricSetup;
		}

		// `changes` includes whole payload, even if the specific part was not changed (use `hasChanges` for that)
		return { hasChanges, changes };
	}

	_checkBlockPreviewChanges() {
		const changes: BlockChangesPayload[] = [],
			statesToRemove: BlockState[] = [];

		let hasChanges = false;

		this.blockStates.forEach((blockState) => {
			const blockCM = blockState.block;

			if (blockCM.isDeletedState()) {
				// remove the block
				statesToRemove.push(blockState);
				return;
			}

			const hasChangesOnBlockState = Object.keys(blockState.changes).length > 0;

			if (hasChangesOnBlockState) {
				hasChanges = true;
			}

			if (
				hasChangesOnBlockState ||
				(blockCM.changes && Object.keys(blockCM.changes).length)
			) {
				// first find which changes we already have saved
				let blockChanges: BlockChanges = {};
				if (this.board.isPreview && blockCM.changes) {
					blockChanges = copyObject(blockCM.changes);
				}

				// merge changes on blockstate with saved changes
				Object.keys(blockState.changes).forEach((changedProp) => {
					const typedChangedProp = changedProp as keyof BlockChanges;
					if (typeof blockState.changes[typedChangedProp] === 'undefined') {
						delete blockChanges[typedChangedProp];
					} else {
						const value = blockState.changes[typedChangedProp];
						Object.assign(blockChanges, { [typedChangedProp]: value });
					}
				});

				changes.push({
					id: blockState.id!,
					originateFrom: (blockCM.isPreview ? blockCM.originateFrom : blockCM.id)!,
					changes: blockChanges,
				});

				// clear the changes
				blockState.changes = {};
			}
		});

		this.blockStates = this.blockStates.filter((bs) => !statesToRemove.includes(bs));

		// `changes` includes whole payload, even if the specific part was not changed (use `hasChanges` for that)
		return { hasChanges, changes };
	}

	_getChangesPayload(forCopy = false) {
		const payload: RequestPayload = {};
		// board
		const boardChanges = this._checkBoardPreviewChanges();
		if (forCopy || boardChanges.hasChanges) {
			payload.boardPreview = { changes: boardChanges.changes };
		}

		// blocks
		const blockChanges = this._checkBlockPreviewChanges();
		if (forCopy || blockChanges.hasChanges) {
			payload.blockPreviews = blockChanges.changes;
		}

		return payload;
	}

	_subscribeToPreviewChannel(previewHash: string) {
		return this.mmBoardPreviewsService.getPromiseAfterPreviewDataChannelSubscribed(previewHash);
	}

	_createNewPreview(asCopy = false) {
		this.isMyPreview = true;

		// getting hash and establishing pusher channel
		let promise = new Promise((resolve, reject) => {
			this.mmBoardPreviewsService.getNewPreviewHash().then(
				(previewHash) => {
					return this._subscribeToPreviewChannel(previewHash).then(resolve, reject);
				},
				() => {
					reject('hash');
				},
			);

			if (asCopy) {
				// remove previous channel
				this.socketService.unsubscribeFromBoardPreview(this.board.previewHash!);
			}
		}).then(
			(previewHash) => {
				return previewHash;
			},
			(reason: string) => {
				this._showUserError(reason);
			},
		);

		// do POST /d/previews/<hash> request
		promise = promise.then((rawPreviewHash) => {
			if (!rawPreviewHash) {
				this._updatePreviewPromise = null; // so user can try again
				return;
			}

			const previewHash = rawPreviewHash as string;

			const data = this._getChangesPayload(asCopy);

			if (!data.boardPreview) {
				data.boardPreview = {};
			}

			// first requests must not include ids (which are from original)
			// instead, they must be in originateFrom
			data.boardPreview.originateFrom = +this.board.boardIdForPublicUrl!;
			(data.blockPreviews || []).forEach((blockPreview) => {
				delete blockPreview.id;
			});

			return this.requestService
				.api<APIData.Board.PreviewCreateResponse>('POST', `/previews/${previewHash}`, data)
				.then(
					(payload) => {
						this.onPreviewHashChange(previewHash);

						// payload = {"previewBoard": {"id":123,"previewHash":"abc123","name":"CDR Databoards test","color":"#143660","order":97,"fromTemplate":null,"users":[11,20,3,14,4,6,16,27,28],"type":0,"isRandom":true,"favorite":false,"hidden":false,"token":"cd7a515b8cec0fdc3c222680ddb34b1b05e7351b7","image":null,"themeId":1,"access":1,"loopIds":[],"password":null,"settings":{"loopDelay":-1,"restrictIps":[],"view":7,"tags":[51]}}, "previewBlocks": [{"id":4010111,"previewHash":"abc123","name":"Number block Preview!","type":1,"order":0,"boardId":null,"parentId":null,"icon":0,"settings":{"isStacked":false,"recommended":false,"showOther":true,"showTotal":true,"position":{"x":3,"y":0,"w":2,"h":2}},"originateFrom":null,"shortName":"MTD","additionalIntervals":[]}]};
						// payload.previewBoard.boardId = this.board.id;
						// payload.previewBoard.previewHash = previewHash;
						// payload.previewBlocks.forEach((bl) => bl.previewHash = previewHash);

						return payload;
					},
					(errorData) => {
						this._showUserError('request');
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
				.then((payload) => {
					if (!payload) {
						this._updatePreviewPromise = null; // so user can try again
						return;
					}

					this.mmBoardPreviewsService.markPreviewRequested(previewHash);

					this.storeService.pushPayload(payload);

					const boardCM = this.mmBoardPreviewsService.findModelByHash(previewHash)!;

					// COPY blockStates with new IDs and old changes
					const blockStates: BlockState[] = [];

					payload.blockPreviews.forEach((blockPreview) => {
						const blockCM = boardCM.allBlockObjs.find(
							(item) => item.id === String(blockPreview.id),
						);

						if (blockCM) {
							const oldBlockState = this.blockStates.find(
									(item) => item.id === blockPreview.originateFrom,
								),
								blockState = this.createNewBlockState(blockCM);

							if (oldBlockState) {
								if (Object.keys(oldBlockState.changes).length) {
									blockState.changes = copyObject(oldBlockState.changes);
								}
								if (oldBlockState.index) {
									blockState.index = oldBlockState.index;
								}
							}

							blockStates.push(blockState);
						}

						// HACK: if we don't have a sample for this blockPreview yet, take the old one from the block
						// https://app.asana.com/0/inbox/8014827585024
						const newSample = (payload.samples || []).find(
							(item) => item.id === blockPreview.id,
						);
						if (!newSample) {
							const oldSample = this.storeService.peekRecord(
								ModelName.SAMPLE,
								blockPreview.originateFrom,
							) as SampleRecord;
							if (oldSample) {
								const sample =
									oldSample.serialize() as RecordData.Sample.Serialized;
								sample.id = String(blockPreview.id); // the store stringifies ids anyway
								this.storeService.pushPayload({ sample });
							}
						}
					});

					this.board = boardCM;
					this.blockStates = blockStates;
				});
		});

		return promise;
	}

	_updateExistingPreview() {
		const previewHash = this.board.previewHash;

		if (!previewHash) {
			return;
		}

		const data = this._getChangesPayload();

		if (!Object.keys(length)) {
			return;
		}

		if (data.blockPreviews) {
			// apply changes to preview blocks
			// so we can correctly detect changes occuring while request is in progress
			data.blockPreviews.forEach((blockData) => {
				const blockPreview = this.board.allBlockObjs.find(
					(item) => item.id === String(blockData.id),
				);
				if (blockPreview) {
					blockPreview.directChangesUpdate(blockData.changes);
				}
			});
			// same id can happen when quickly generating changes before the previrew block is saved
			// in that case, we ignore this change
			data.blockPreviews = data.blockPreviews.filter((blockData) => {
				return blockData.id && blockData.id !== +(blockData.originateFrom as string);
			});
		}

		if (data.boardPreview) {
			// apply changes to preview board
			this.board.directPropUpdate({
				changes: data.boardPreview.changes,
			});
		}

		// do PUT /d/previews/<hash> request
		return this.requestService
			.api<RecordData.Payload>('PUT', `/previews/${previewHash}`, data)
			.then(
				(payload) => {
					return payload;
				},
				(errorData) => {
					this._showUserError('request');
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
			.then((payload) => {
				if (payload) {
					this.storeService.pushPayload(payload);
				}
			});
	}

	_showUserError(message: string) {
		this.infolayerService.error(
			`Sorry, there was a problem generating your board preview [${message}]`,
		);
	}

	setPropsFromChanges(isInitialCheck = false) {
		const board = this.board;
		if (board.isPreview && board.changes) {
			if (
				board.changes.masterDateRange &&
				(isInitialCheck || !this._selectedMasterDateRange)
			) {
				// preselect master date range
				this._selectedMasterDateRange = DateRange.createFromFormula(
					board.changes.masterDateRange.dateRange,
				);
			}
			if (
				// preselect dimensions
				board.changes.masterMetricSetup?.dimensions &&
				(isInitialCheck || !this._selectedMasterDimensions)
			) {
				this._selectedMasterDimensions = board.changes.masterMetricSetup.dimensions;
			}
			if (
				board.changes.masterMetricSetup?.filters &&
				(isInitialCheck || this._selectedMasterFilters === undefined)
			) {
				// preselect dimension values
				this._selectedMasterFilters = board.changes.masterMetricSetup.filters;
			}
		}
	}

	private revertingToOriginal = false;

	resetToOriginal() {
		this.revertingToOriginal = true; // flag to avoid stateChanged trigger

		if (!this.board) {
			return;
		}

		if (this.board.previewHash) {
			this.socketService.unsubscribeFromBoardPreview(this.board.previewHash);
		}

		const defaultBoard = this.storeService.peekRecord(
			'board',
			this.board.boardId!,
		) as BoardRecord;
		if (defaultBoard) {
			this.board = this.mmBoardsService.createModelFromRecord(defaultBoard);
		}

		this.isMyPreview = false;
		this._updatePreviewPromise = null;
		this.blockStates = [];
		this._selectedMasterFilters = undefined;
		this._selectedMasterDimensions = undefined;
		this.selectedMasterDateRange = null;

		this.onPreviewHashChange(null);

		this.revertingToOriginal = false;
	}

	// eslint-disable-next-line ember/classic-decorator-hooks
	destroy(deletePreview = false) {
		// close board preview channel
		const hash = this.board ? this.board.previewHash : null;
		if (hash) {
			this.socketService.unsubscribeFromBoardPreview(hash);

			if (deletePreview) {
				void this.requestService.api('DELETE', `/previews/${hash}`).catch();
			}
		}
	}

	static createFromBoard(board: Board | MinimumViableBoard) {
		const boardState = new BoardState({ board });

		boardState.setPropsFromChanges(true);

		return boardState;
	}

	static createForSingleBlock(block: Block) {
		const board = {
			allBlockObjs: [block],
			directPropUpdate: () => {
				/* nothing */
			},
		} as MinimumViableBoard;

		const boardState = BoardState.createFromBoard(board);
		boardState.explicitlyDisableAllPreviews = true;
		boardState.selectBlock(block.id);

		return boardState;
	}
}
